import { initialState } from '@/Helpers.js';

export function configureFreshchatUser(widget, host) {
    widget.setExternalId(host.user.id.toString());
    widget.user.setFirstName(host.user.first_name);
    widget.user.setLastName(host.user.last_name);
    widget.user.setEmail(host.user.email);
    widget.user.setProperties({
        cf_external_id: host.user.external_id || '',
        cf_primary_group: host.primaryGroup || '',
        cf_user_role: host.userRole,
        cf_institution: initialState('InstitutionSettings.name', ''),
        cf_user_id: host.user.id.toString(),
    });
}

export function chatOpenAllowed(host) {
    return host.isHelpPage && !host.$store.getters['User/hasActiveAttempt'];
}

export function clearQueuedChatOpen(host) {
    host.pendingOpen = false;
    host.expectingOpen = false;
    host.clearPendingOpenTimer();
}

export function cancelQueuedChatOpen(host) {
    clearQueuedChatOpen(host);
    if (host.isInitialized || host.widgetReadyPromise) host.destroyChatbot();
}

export function restoreAlreadyOpenWidget(widget, isReady, host) {
    if (!isReady || !widget?.isOpen?.()) {
        return false;
    }

    host.pendingOpen = false;
    host.expectingOpen = false;
    host.prepareWidgetForOpen();

    return true;
}

export function bindFreshchatOpenEvents(widget, host) {
    widget.on('widget:opened', () => {
        host.expectingOpen = false;
        host.widgetOpenSeen = true;
        host.prepareWidgetForOpen();
    });
    widget.on('widget:closed', () => {
        if (!host.expectingOpen) {
            host.hideChatbot();
        }
    });
}

const CHAT_OPEN_WATCH_MS = 15000;
const CHAT_OPEN_POLL_MS = 250;
const CHAT_OPEN_RETRY_MS = 2000;
const CHAT_OPEN_WITHOUT_FRAME_MS = CHAT_OPEN_POLL_MS;

/**
 * Freshchat ignores fcWidget.open() until its iframe exists, and a third open()
 * toggles the panel closed. Open once the frame is up, retry once, and keep
 * watching until the panel actually opens. A slow widget can take 10–15s.
 *
 * @param {object} options
 * @param {() => boolean} options.shouldContinue
 * @param {() => boolean} options.canOpen
 * @param {() => void} options.open
 * @param {() => void} [options.onOpened]
 * @param {() => void} [options.onUnconfirmed]
 * @param {() => void} options.onDone
 * @param {boolean} [options.openImmediately]
 * @returns {() => void} cancel
 */
export function schedulePendingChatOpen({
    shouldContinue,
    canOpen,
    open,
    onOpened,
    onUnconfirmed,
    onDone,
    didOpen,
    openImmediately = false,
}) {
    const timers = [];
    let stopped = false;
    let openCalls = 0;
    let openedAt = 0;
    const startedAt = Date.now();

    const clear = () => {
        stopped = true;
        timers.splice(0).forEach(id => clearTimeout(id));
    };

    const finish = callback => {
        callback?.();
        onDone();
        clear();
    };

    const panelIsOpen = () => didOpen?.() === true || window.fcWidget?.isOpen?.() === true;

    const frameReady = () => Boolean(document.getElementById('fc_frame')?.querySelector('iframe'));

    const canCallOpen = () => canOpen() && window.fcWidget && typeof window.fcWidget.open === 'function';

    const tick = () => {
        if (stopped) return;

        if (!shouldContinue()) {
            finish();
            return;
        }

        if (panelIsOpen()) {
            finish(onOpened);
            return;
        }

        const elapsed = Date.now() - startedAt;
        if (elapsed >= CHAT_OPEN_WATCH_MS) {
            finish(onUnconfirmed);
            return;
        }

        const readyToOpen = openImmediately || frameReady() || elapsed >= CHAT_OPEN_WITHOUT_FRAME_MS;
        const retryDue = openCalls === 1 && Date.now() - openedAt >= CHAT_OPEN_RETRY_MS;

        if (canCallOpen() && readyToOpen && (openCalls === 0 || retryDue)) {
            open();
            openCalls += 1;
            openedAt = Date.now();

            if (panelIsOpen()) {
                finish(onOpened);
                return;
            }
        }

        timers.push(setTimeout(tick, CHAT_OPEN_POLL_MS));
    };

    if (openImmediately) {
        tick();
    } else {
        timers.push(setTimeout(tick, 0));
    }

    return clear;
}

export function rememberPendingChatOpen(host) {
    if (host.pendingOpenDeadline) {
        clearTimeout(host.pendingOpenDeadline);
    }

    host.pendingOpenDeadline = armChatOpenDeadline(host);
}

export function resumeQueuedChatOpen(host, unconfirmedEvent, openImmediately = false) {
    if (!host.pendingOpen || !chatOpenAllowed(host)) {
        clearQueuedChatOpen(host);
        return;
    }

    host.widgetOpenSeen = false;
    host.clearPendingOpenTimer();
    host.pendingOpenTimer = schedulePendingChatOpen({
        shouldContinue: () => host.pendingOpen && chatOpenAllowed(host),
        canOpen: () => host.isWidgetReady,
        didOpen: () => host.widgetOpenSeen === true,
        open: () => {
            host.bindWidgetEvents();
            window.fcWidget.open();
        },
        onOpened: () => {
            host.expectingOpen = false;
            host.prepareWidgetForOpen();
            host.publishChatbotStatus('ready', { opened: true });
        },
        onUnconfirmed: () => {
            host.expectingOpen = false;
            window.dispatchEvent(new CustomEvent(unconfirmedEvent));
        },
        onDone: () => {
            host.pendingOpen = false;
            if (!chatOpenAllowed(host)) host.expectingOpen = false;
        },
        openImmediately,
    });
}

const CHAT_LOAD_TIMEOUT_MS = 15000;

/**
 * Give a press made while Ollie is loading up to 15s for Freshchat to report
 * ready before calling chat unavailable. On a slow connection the widget can
 * take several seconds; giving up sooner dropped the press and showed
 * "Chat is unavailable" just before the widget became ready. A script that
 * fails to load still reports unavailable straight away through onerror.
 *
 * @param {object} host
 * @returns {number} timeout id
 */
export function armChatOpenDeadline(host) {
    return setTimeout(() => {
        if (!host.pendingOpen || host.isWidgetReady) {
            return;
        }

        host.pendingOpen = false;
        host.expectingOpen = false;
        host.publishChatbotStatus('unavailable');
    }, CHAT_LOAD_TIMEOUT_MS);
}
